import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import apiClient from '../services/apiClient';
import { userService } from '../services/userService';
import { getAvatarUrl } from '../utils/avatarUtils';
import Modal, { ConfirmModal } from './Modal';
import { Button as UIButton } from './ui/button';
import { Input as UIInput } from './ui/input';
import { Label as UILabel } from './ui/label';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import ChangePasswordSheet from './ChangePasswordSheet';

const DEFAULT_AVATAR = 'http://localhost:3001/uploads/demo-avatar.png';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 30px;
  color: var(--color-black);
`;

const ProfileCard = styled.div`
  background: var(--color-white);
  border: 2px solid var(--color-light-gray);
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const AvatarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
`;

const AvatarContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Avatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
`;

const HiddenFileInput = styled.input`
  display: none;
`;


const AvatarButton = styled.button<{ $danger?: boolean }>`
  background-color: var(--color-black);
  color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--color-white);
    color: var(--color-black);
  }
`;

const EditSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EditButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const CropperModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1003;
`;

const CropperContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 0;
  max-width: 700px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const CropperImage = styled.img`
  max-width: 100%;
  max-height: 500px;
  display: block;
`;

const CropperButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: center;
`;

const CropperButton = styled.button<{ $primary?: boolean }>`
  background-color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-light-gray)'};
  color: ${props => props.$primary ? 'white' : 'var(--color-black)'};
  border: none;
  padding: 10px 20px;
  border-radius: 0;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    background-color: ${props => props.$primary ? 'var(--color-blue)' : 'var(--color-gray)'};
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const Username = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-black);
  margin-bottom: 5px;
`;

const UserEmail = styled.p`
  font-size: 1rem;
  color: var(--color-gray);
`;

const EditButton = styled.button`
  background-color: var(--color-black);
  color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--color-white);
    color: var(--color-black);
  }
`;

const QuotaContainer = styled.div`
  padding: 0;
`;

const QuotaSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const QuotaInfo = styled.div`
  flex: 1;
`;

const QuotaTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-gray);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const QuotaValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-black);
  line-height: 1;
  margin-bottom: 8px;
`;

const QuotaSubtext = styled.div`
  font-size: 0.9rem;
  color: var(--color-gray);
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background-color: var(--color-light-gray);
  margin-top: 12px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $percentage: number }>`
  width: ${props => props.$percentage}%;
  height: 100%;
  background-color: var(--color-black);
  transition: width 0.3s ease;
`;

const QuotaActions = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PurchaseButton = styled.button`
  background-color: var(--color-black);
  color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: var(--color-white);
    color: var(--color-black);
  }
`;

const DetailsToggle = styled.button`
  background-color: var(--color-white);
  color: var(--color-black);
  border: 2px solid var(--color-black);
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: var(--color-light-gray);
  }
`;

const QuotaDetails = styled.div<{ $isOpen: boolean }>`
  border-top: ${props => props.$isOpen ? '2px solid var(--color-light-gray)' : 'none'};
  padding: ${props => props.$isOpen ? '24px 0 0 0' : '0'};
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailTitle = styled.h4`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-gray);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const DetailContent = styled.div`
  color: var(--color-black);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-top: 12px;
`;

const PricingCard = styled.div`
  border: 2px solid var(--color-black);
  padding: 20px;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: var(--color-black);
    color: var(--color-white);
    
    & > * {
      color: var(--color-white);
    }
  }
`;

const PricingAmount = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-black);
  margin-bottom: 8px;
`;

const PricingLabel = styled.div`
  font-size: 0.9rem;
  color: var(--color-gray);
  font-weight: 500;
`;

const PricingValue = styled.div`
  font-size: 0.85rem;
  color: var(--color-gray);
  margin-top: 4px;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-black);
  margin-bottom: 20px;
`;


const Button = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  background-color: var(--color-black);
  color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--color-white);
    color: var(--color-black);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EmailVerificationGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SmallButton = styled.button`
  background-color: var(--color-black);
  color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 12px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: var(--color-white);
    color: var(--color-black);
  }
  
  &:disabled {
    background-color: var(--color-gray);
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #FF6B6B;
  font-size: 0.9rem;
  margin-top: 5px;
`;

const InfoMessage = styled.div`
  color: var(--color-blue);
  font-size: 0.9rem;
  margin-top: 5px;
`;


interface ProfileScreenProps {
  user?: any;
}

const ProfileScreen: React.FC<ProfileScreenProps> = ({ user: propUser }) => {
  const [user, setUser] = useState({
    id: '',
    username: '',
    email: '',
    avatar: '/uploads/demo-avatar.png',
    provider: '',
    emailVerified: false,
    createdAt: '',
    updatedAt: '',
    lastLoginAt: '',
    credits: 0
  });

  const [quota] = useState({
    total: 100,
    used: 25,
    remaining: 75
  });
  
  const [showQuotaDetails, setShowQuotaDetails] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ message: string; onConfirm: () => void; showCancel?: boolean; isDanger?: boolean } | null>(null);

  const [editForm, setEditForm] = useState({
    username: '',
    email: ''
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({ unit: '%', x: 25, y: 25, width: 50, height: 50 });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [loading, setLoading] = useState(true);
  const [originalEmail, setOriginalEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isEmailModified, setIsEmailModified] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  // 加载用户数据
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        
        // 优先使用传入的用户数据（Google 登录等）
        if (propUser) {
          setUser({
            id: propUser.id,
            username: propUser.username,
            email: propUser.email,
            avatar: propUser.avatar || '/uploads/demo-avatar.png',
            provider: propUser.provider || 'firebase',
            emailVerified: propUser.emailVerified || false,
            createdAt: propUser.createdAt || new Date().toISOString(),
            updatedAt: propUser.updatedAt || new Date().toISOString(),
            lastLoginAt: propUser.lastLoginAt || new Date().toISOString(),
            credits: propUser.credits || 0
          });
          setLoading(false);
          return;
        }
        
        // 如果没有传入数据，从本地存储获取
        const localUser = await userService.getCurrentUser();
        
        if (localUser) {
          setUser({
            id: localUser.id,
            username: localUser.username,
            email: localUser.email,
            avatar: localUser.avatar || '/uploads/demo-avatar.png',
            provider: localUser.provider || 'firebase',
            emailVerified: localUser.emailVerified || false,
            createdAt: localUser.createdAt || new Date().toISOString(),
            updatedAt: localUser.updatedAt || new Date().toISOString(),
            lastLoginAt: localUser.lastLoginAt || new Date().toISOString(),
            credits: localUser.credits || 0
          });
        }
        
        // 然后尝试从API获取最新数据
        try {
          const userData = await userService.getUserProfile();
          setUser({
            id: userData.id,
            username: userData.username,
            email: userData.email,
            avatar: userData.avatar || '/uploads/demo-avatar.png',
            provider: userData.provider || 'firebase',
            emailVerified: userData.emailVerified || false,
            createdAt: userData.createdAt || new Date().toISOString(),
            updatedAt: userData.updatedAt || new Date().toISOString(),
            lastLoginAt: userData.lastLoginAt || new Date().toISOString(),
            credits: userData.credits || 0
          });
        } catch (apiError) {
          console.error('API call failed:', apiError);
          // 如果API调用失败，使用本地数据
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [propUser?.id]);

  // 监听邮箱更新事件
  useEffect(() => {
    const handleEmailUpdated = (event: CustomEvent) => {
      console.log('📧 Email update event received:', event.detail);
      const { email, emailVerified } = event.detail;
      
      setUser(prevUser => {
        if (prevUser) {
          return {
            ...prevUser,
            email: email,
            emailVerified: emailVerified
          };
        }
        return prevUser;
      });
      
      // 更新编辑表单中的邮箱
      setEditForm(prevForm => ({
        ...prevForm,
        email: email
      }));
      
      // 更新原始邮箱
      setOriginalEmail(email);
    };

    window.addEventListener('emailUpdated', handleEmailUpdated as EventListener);
    
    return () => {
      window.removeEventListener('emailUpdated', handleEmailUpdated as EventListener);
    };
  }, []);

  const handleEdit = () => {
    setEditForm({
      username: user.username,
      email: user.email
    });
    setOriginalEmail(user.email);
    setUploadError(null);
    setIsEmailModified(false);
  };

  const handleModifyEmail = () => {
    setIsEmailModified(true);
    setUploadError(null);
  };


  const handleSave = () => {
    // 检查email是否修改
    const emailChanged = editForm.email.toLowerCase() !== originalEmail.toLowerCase();
    

    // 显示确认对话框
    setConfirmAction({
      message: 'Are you sure you want to save these changes?',
      onConfirm: async () => {
        setIsSaving(true);
        setUploadError(null);

        try {
          // 使用 Firestore 更新用户信息
          const { firestoreService } = await import('../services/firestoreService');
          
          if (emailChanged) {
            // 🔥 使用 Firebase 的安全邮箱更新方式
            const { verifyBeforeUpdateEmail } = await import('firebase/auth');
            const { auth } = await import('../services/firebaseConfig');
            
            const currentUser = auth.currentUser;
            if (!currentUser) {
              throw new Error('用户未登录');
            }
            
            // 发送邮箱验证邮件
            await verifyBeforeUpdateEmail(currentUser, editForm.email);
            
            // 只更新用户名到 Firestore（邮箱会在用户点击验证链接后自动更新）
            await firestoreService.updateUser(user.id, {
              username: editForm.username
            });
            
            // 更新本地状态（只更新用户名，但显示新邮箱给用户看）
            const updatedUser = {
              ...user,
              username: editForm.username
              // 注意：email 保持原值，直到用户点击验证链接
            };
            setUser(updatedUser);
            
            // 更新 localStorage
            const currentUserInfo = localStorage.getItem('userInfo');
            if (currentUserInfo) {
              const userData = JSON.parse(currentUserInfo);
              userData.username = editForm.username;
              // 注意：不更新 email，保持原值
              localStorage.setItem('userInfo', JSON.stringify(userData));
            }
            
            // 显示邮箱验证提示
            setConfirmAction({
              message: `Verification email has been sent to ${editForm.email}. Please check your email and click the link to complete the email change.`,
              onConfirm: () => {},
              showCancel: false
            });
            setShowConfirmModal(true);
            
          } else {
            // 只更新用户名
            await firestoreService.updateUser(user.id, {
              username: editForm.username
            });
            
            // 更新本地状态
            const updatedUser = {
              ...user,
              username: editForm.username
            };
            setUser(updatedUser);
            
            // 更新 localStorage
            const currentUserInfo = localStorage.getItem('userInfo');
            if (currentUserInfo) {
              const userData = JSON.parse(currentUserInfo);
              userData.username = editForm.username;
              localStorage.setItem('userInfo', JSON.stringify(userData));
            }
            
            // 显示成功消息
            setConfirmAction({
              message: 'Your profile has been updated successfully!',
              onConfirm: () => {},
              showCancel: false
            });
            setShowConfirmModal(true);
          }
          
          // 触发自定义事件通知其他组件用户信息已更新
          window.dispatchEvent(new CustomEvent('userInfoUpdated', { 
            detail: { 
              username: editForm.username,
              email: user.email // 保持原邮箱，直到验证完成
            } 
          }));
            
            setIsEmailModified(false);
            
            // 关闭Sheet
            setIsSheetOpen(false);
            
            // 显示成功消息
            setConfirmAction({
              message: 'Your profile has been updated successfully!',
              onConfirm: () => {},
              showCancel: false
            });
            setShowConfirmModal(true);
        } catch (error: any) {
          console.error('Update profile error:', error);
          
          // 处理 Firebase 错误
          let errorMessage = 'Update failed';
          if (error.code === 'auth/requires-recent-login') {
            errorMessage = 'This is a sensitive operation. For your account security, please log in again before trying to modify your email.';
          } else if (error.code === 'auth/email-already-in-use') {
            errorMessage = 'This email is already in use by another account.';
          } else if (error.code === 'auth/invalid-email') {
            errorMessage = 'Invalid email format.';
          } else if (error.code === 'auth/user-token-expired') {
            errorMessage = 'Your login has expired, please log in again to modify your email.';
            // 显示重新登录提示模态框
            setConfirmAction({
              message: 'Your login has expired. For security reasons, please log in again. The system will automatically redirect to the login page in 3 seconds.',
              onConfirm: () => {
                localStorage.removeItem('userInfo');
                window.location.href = '/login';
              },
              showCancel: false
            });
            setShowConfirmModal(true);
            
            // 自动跳转
            setTimeout(() => {
              localStorage.removeItem('userInfo');
              window.location.href = '/login';
            }, 3000);
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          setUploadError(errorMessage);
        } finally {
          setIsSaving(false);
        }
      },
      showCancel: true
    });
    setShowConfirmModal(true);
  };


  const handlePurchase = (credits: number, price: string) => {
    setShowPurchaseModal(false);
    setConfirmAction({
      message: `Confirm purchase of ${credits} generation${credits > 1 ? 's' : ''} for ${price}?`,
      onConfirm: () => {
        // TODO: 实现支付逻辑
        setConfirmAction({
          message: `Purchase confirmed: ${credits} generations for ${price}. You will be redirected to payment shortly.`,
          onConfirm: () => {},
          showCancel: false
        });
        setShowConfirmModal(true);
      },
      showCancel: true
    });
    setShowConfirmModal(true);
  };

  const handleDeleteAccount = () => {
    setConfirmAction({
      message: 'Are you sure you want to delete your account? This action cannot be undone!',
      onConfirm: () => {
        // TODO: 实现账户删除逻辑
        setConfirmAction({
          message: 'Account deletion feature is not yet implemented. Please contact support for assistance.',
          onConfirm: () => {},
          showCancel: false
        });
        setShowConfirmModal(true);
      },
      showCancel: true,
      isDanger: true
    });
    setShowConfirmModal(true);
  };

  const handleChangePassword = () => {
    setIsChangePasswordOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // 验证文件大小 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    // 创建图片预览URL
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowCropper(true);
    setUploadError(null);
  };

  const handleCropConfirm = async (croppedImageBlob: Blob) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      // 将图片转换为 base64 字符串
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
      });
      
      reader.readAsDataURL(croppedImageBlob);
      const base64String = await base64Promise;
      
      console.log('✅ Avatar converted to base64, length:', base64String.length);
      
      // 更新用户头像（直接使用 base64）
      const updatedUser = {
        ...user,
        avatar: base64String
      };
      setUser(updatedUser);
      
      // 更新 Firestore 中的用户数据
      const { firestoreService } = await import('../services/firestoreService');
      await firestoreService.updateUser(user.id, { avatar: base64String });
      
      // 更新 localStorage
      const currentUser = localStorage.getItem('userInfo');
      if (currentUser) {
        const userData = JSON.parse(currentUser);
        userData.avatar = base64String;
        localStorage.setItem('userInfo', JSON.stringify(userData));
      }
      
      // 触发自定义事件通知其他组件
      window.dispatchEvent(new CustomEvent('avatarUpdated', { 
        detail: { avatar: base64String } 
      }));
      
      setShowCropper(false);
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
        setSelectedImage(null);
      }
    } catch (error: any) {
      console.error('Avatar upload error:', error);
      setUploadError(error.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
      setSelectedImage(null);
    }
  };

  const getCroppedImg = (image: HTMLImageElement, crop: PixelCrop): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // 设置画布为正方形（取裁剪区域的宽高）
    const size = Math.min(crop.width, crop.height);
    canvas.width = size;
    canvas.height = size;

    // 清除画布（透明背景）
    ctx.clearRect(0, 0, size, size);

    // 创建圆形裁剪路径
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    // 绘制图像
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      size,
      size
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        }
      }, 'image/png');
    });
  };

  const handleCropComplete = async () => {
    if (!imgRef.current || !completedCrop) {
      return;
    }

    try {
      const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop);
      await handleCropConfirm(croppedImageBlob);
    } catch (error) {
      console.error('Error cropping image:', error);
      setUploadError('Failed to crop image');
    }
  };


  const triggerFileInput = () => {
    const fileInput = document.getElementById('avatar-upload') as HTMLInputElement;
    fileInput?.click();
  };

  if (loading) {
    return (
      <Container>
        <Title>Personal Center</Title>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>Loading user data...</div>
        </div>
      </Container>
    );
  }

  const usagePercentage = (quota.used / quota.total) * 100;

  return (
    <Container>
      <Title>Personal Center</Title>

      <ProfileCard>
        <AvatarSection>
          <AvatarContainer>
            <Avatar 
              src={getAvatarUrl(user.avatar)} 
              alt="User Avatar"
              onError={(e) => {
                // 头像加载失败时显示默认头像
                e.currentTarget.src = DEFAULT_AVATAR;
              }}
            />
            <HiddenFileInput
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
            />
          </AvatarContainer>
          <UserInfo>
            <Username>{user.username}</Username>
            <UserEmail>{user.email}</UserEmail>
          </UserInfo>
          <EditSection>
            <EditButtonGroup>
              <Sheet 
                open={isSheetOpen}
                onOpenChange={(open) => {
                  setIsSheetOpen(open);
                  if (open) {
                    handleEdit();
                  }
                }}
              >
                <SheetTrigger asChild>
                  <EditButton>
                    Edit
                  </EditButton>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Edit profile</SheetTitle>
                  </SheetHeader>
                  <div className="grid flex-1 auto-rows-min gap-6 px-4">
                    <SheetDescription>
                      Make changes to your profile here. Click save when you&apos;re done.
                    </SheetDescription>
                    <div className="grid gap-3">
                      <UILabel htmlFor="sheet-demo-name">Username</UILabel>
                      <UIInput 
                        id="sheet-demo-name" 
                        value={editForm.username}
                        onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-3">
                      <UILabel htmlFor="sheet-demo-email">Email</UILabel>
                      <EmailVerificationGroup>
                        <UIInput
                          id="sheet-demo-email"
                          type="email"
                          value={editForm.email}
                          onChange={(e) => {
                            setEditForm({ ...editForm, email: e.target.value });
                          }}
                          style={{ flex: 1 }}
                          disabled={!isEmailModified}
                        />
                        {!isEmailModified ? (
                          <SmallButton 
                            type="button"
                            onClick={handleModifyEmail}
                          >
                            Modify
                          </SmallButton>
                        ) : null}
                      </EmailVerificationGroup>
                    </div>
                    {/* 邮箱修改时的操作按钮 */}
                    {editForm.email.toLowerCase() !== originalEmail.toLowerCase() && (
                      <div className="grid gap-3" style={{ marginTop: '20px' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <UIButton 
                            type="button" 
                            onClick={async () => {
                              try {
                                console.log('🔍 开始发送邮箱验证邮件...');
                                console.log('📧 目标邮箱:', editForm.email);
                                
                                const { verifyBeforeUpdateEmail } = await import('firebase/auth');
                                const { auth } = await import('../services/firebaseConfig');
                                const currentUser = auth.currentUser;
                                
                                console.log('👤 当前用户:', currentUser);
                                console.log('📧 当前用户邮箱:', currentUser?.email);
                                console.log('✅ 用户邮箱已验证:', currentUser?.emailVerified);
                                
                                if (currentUser) {
                                  console.log('🚀 调用 verifyBeforeUpdateEmail...');
                                  await verifyBeforeUpdateEmail(currentUser, editForm.email);
                                  console.log('✅ 验证邮件发送成功！');
                                  
                                  setUploadError('');
                                  setConfirmAction({
                                    message: `邮箱验证邮件已发送至 ${editForm.email}，请查收邮件并点击链接以完成邮箱更改。`,
                                    onConfirm: () => {},
                                    showCancel: false
                                  });
                                  setShowConfirmModal(true);
                                } else {
                                  console.error('❌ 用户未登录');
                                  setUploadError('用户未登录，请重新登录后重试');
                                }
                              } catch (error: any) {
                                console.error('❌ 发送验证邮件失败:', error);
                                console.error('错误代码:', error.code);
                                console.error('错误消息:', error.message);
                                
                                let errorMessage = 'Failed to send verification email';
                                if (error.code === 'auth/requires-recent-login') {
                                  errorMessage = 'This is a sensitive operation. For your account security, please log in again before trying to modify your email.';
                                } else if (error.code === 'auth/email-already-in-use') {
                                  errorMessage = 'This email is already in use by another account.';
                                } else if (error.code === 'auth/invalid-email') {
                                  errorMessage = 'Invalid email format.';
                                } else if (error.code === 'auth/too-many-requests') {
                                  errorMessage = 'Too many requests, please try again later.';
                                } else if (error.code === 'auth/operation-not-allowed') {
                                  errorMessage = 'Email verification is not enabled, please contact the administrator.';
                                } else if (error.code === 'auth/user-token-expired') {
                                  errorMessage = 'Your login has expired, please log in again to modify your email.';
                                  // 显示重新登录提示模态框
                                  setConfirmAction({
                                    message: 'Your login has expired. For security reasons, please log in again. The system will automatically redirect to the login page in 3 seconds.',
                                    onConfirm: () => {
                                      localStorage.removeItem('userInfo');
                                      window.location.href = '/login';
                                    },
                                    showCancel: false
                                  });
                                  setShowConfirmModal(true);
                                  
                                  // 自动跳转
                                  setTimeout(() => {
                                    localStorage.removeItem('userInfo');
                                    window.location.href = '/login';
                                  }, 3000);
                                } else {
                                  errorMessage = `发送失败: ${error.message || error.code}`;
                                }
                                setUploadError(errorMessage);
                              }
                            }}
                            variant="outline"
                            style={{ flex: 1 }}
                          >
                            Send Verification Email
                          </UIButton>
                        </div>
                      </div>
                    )}
                    
                    {/* 头像修改按钮 - 始终显示 */}
                    <div className="grid gap-3" style={{ marginTop: '20px' }}>
                      <AvatarButton onClick={triggerFileInput} disabled={isUploading}>
                        {isUploading ? 'Uploading...' : 'Change Avatar'}
                      </AvatarButton>
                    </div>
                    {uploadError && (
                      <ErrorMessage>{uploadError}</ErrorMessage>
                    )}
                  </div>
                  <SheetFooter>
                    <UIButton type="button" onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save changes'}
                    </UIButton>
                    <SheetClose asChild>
                      <UIButton variant="outline">Close</UIButton>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </EditButtonGroup>
          </EditSection>
        </AvatarSection>

        <div>
          <p style={{ color: 'var(--color-gray)', marginBottom: '20px' }}>
            Join Date: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      </ProfileCard>

      <ProfileCard>
        <SectionTitle>Generation Credits</SectionTitle>
        <QuotaContainer>
          <QuotaSummary>
            <QuotaInfo>
              <QuotaTitle>Available Credits</QuotaTitle>
              <QuotaValue>{quota.remaining}</QuotaValue>
              <QuotaSubtext>{quota.used} of {quota.total} used</QuotaSubtext>
              <ProgressBarContainer>
                <ProgressBarFill $percentage={usagePercentage} />
              </ProgressBarContainer>
            </QuotaInfo>
            <QuotaActions>
              <PurchaseButton onClick={() => setShowPurchaseModal(true)}>
                Purchase Credits
              </PurchaseButton>
              <DetailsToggle onClick={() => setShowQuotaDetails(!showQuotaDetails)}>
                {showQuotaDetails ? 'Hide' : 'Details'}
              </DetailsToggle>
            </QuotaActions>
          </QuotaSummary>
          
          <QuotaDetails $isOpen={showQuotaDetails}>
            <DetailSection>
              <DetailTitle>How It Works</DetailTitle>
              <DetailContent>
                Each wallpaper generation uses one credit. Purchase credits in bundles to save more. 
                Credits never expire and can be used anytime.
              </DetailContent>
            </DetailSection>
            
            <DetailSection>
              <DetailTitle>Usage Statistics</DetailTitle>
              <DetailContent>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Total Generated</div>
                    <div style={{ color: 'var(--color-gray)', fontSize: '0.9rem' }}>Lifetime</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{quota.used}</div>
                    <div style={{ color: 'var(--color-gray)', fontSize: '0.9rem' }}>This session</div>
                  </div>
                </div>
              </DetailContent>
            </DetailSection>
            
            <DetailSection>
              <DetailTitle>Payment & Subscription</DetailTitle>
              <DetailContent>
                <div style={{ 
                  background: 'var(--color-light-gray)', 
                  padding: '20px', 
                  border: '2px solid var(--color-black)',
                  textAlign: 'center',
                  marginTop: '12px'
                }}>
                  <div style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '600', 
                    color: 'var(--color-black)',
                    marginBottom: '10px'
                  }}>
                    Payment System Coming Soon!
                  </div>
                  <div style={{ 
                    fontSize: '0.95rem', 
                    color: 'var(--color-gray)',
                    lineHeight: '1.5'
                  }}>
                    We're currently working on implementing a comprehensive payment system that will include:
                    <br />• Unlimited daily generations
                    <br />• Premium subscription plans
                    <br />• Credit packages
                    <br />• Advanced features
                    <br /><br />
                    Stay tuned for updates! In the meantime, enjoy your 3 free daily generations.
                  </div>
                </div>
              </DetailContent>
            </DetailSection>
          </QuotaDetails>
        </QuotaContainer>
      </ProfileCard>

      <ProfileCard>
        <SectionTitle>Account Management</SectionTitle>
        <ButtonGroup>
          <Button onClick={handleChangePassword}>
            Change Password
          </Button>
          <Button $danger onClick={handleDeleteAccount}>
            Delete Account
          </Button>
        </ButtonGroup>
      </ProfileCard>

      {/* Purchase Modal */}
      <Modal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        title="Purchase Generation Credits"
      >
        <div style={{ marginBottom: '24px' }}>
          <p style={{ marginBottom: '20px', lineHeight: 1.6 }}>
            Choose a credit package to continue generating wallpapers. Credits never expire.
          </p>
          <PricingGrid>
            <PricingCard onClick={() => handlePurchase(1, 'A$1.00')}>
              <PricingAmount>1</PricingAmount>
              <PricingLabel>Generation</PricingLabel>
              <PricingValue>A$1.00</PricingValue>
            </PricingCard>
            <PricingCard onClick={() => handlePurchase(3, 'A$2.80')}>
              <PricingAmount>3</PricingAmount>
              <PricingLabel>Generations</PricingLabel>
              <PricingValue>A$2.80</PricingValue>
              <div style={{ marginTop: '8px', fontSize: '0.8rem', fontWeight: 600 }}>Save 7%</div>
            </PricingCard>
            <PricingCard onClick={() => handlePurchase(5, 'A$4.50')}>
              <PricingAmount>5</PricingAmount>
              <PricingLabel>Generations</PricingLabel>
              <PricingValue>A$4.50</PricingValue>
              <div style={{ marginTop: '8px', fontSize: '0.8rem', fontWeight: 600 }}>Save 10%</div>
            </PricingCard>
          </PricingGrid>
        </div>
      </Modal>

      {/* Confirm Modal */}
      {confirmAction && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmAction.onConfirm}
          title="Confirm Action"
          message={confirmAction.message}
          cancelText={confirmAction.showCancel !== false ? 'Cancel' : ''}
          isDanger={confirmAction.isDanger || false}
        />
      )}

      {/* 图片裁剪模态框 */}
      {showCropper && selectedImage && (
        <CropperModal>
          <CropperContainer>
            <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>Crop Your Avatar</h3>
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              circularCrop
            >
              <CropperImage
                ref={imgRef}
                src={selectedImage}
                alt="Crop me"
                onLoad={() => {
                  // 智能初始化裁剪区域：居中显示，大小适中
                  if (imgRef.current) {
                    // 计算合适的裁剪框大小（占图片的50%）
                    const cropSize = 50;
                    const x = (100 - cropSize) / 2;
                    const y = (100 - cropSize) / 2;
                    setCrop({ unit: '%', x, y, width: cropSize, height: cropSize });
                  }
                }}
              />
            </ReactCrop>
            <CropperButtons>
              <CropperButton onClick={handleCropCancel}>
                Cancel
              </CropperButton>
              <CropperButton $primary onClick={handleCropComplete} disabled={!completedCrop}>
                {isUploading ? 'Uploading...' : 'Confirm Crop'}
              </CropperButton>
            </CropperButtons>
          </CropperContainer>
        </CropperModal>
      )}

      {/* Change Password Sheet */}
      <ChangePasswordSheet
        open={isChangePasswordOpen}
        onOpenChange={setIsChangePasswordOpen}
      />
    </Container>
  );
};

export default ProfileScreen;
